import { useCallback, useState, useEffect, useMemo } from 'react';
import {
  RtbCampaignManager,
  DefaultRtbCampaignManager,
} from 'core/rtbCampaign/RtbCampaignManager';
import { RtbCampaignBasic } from 'core/rtbCampaign/RtbCampaign';
import { useCallAPI } from 'hooks/useCallAPI';
import { useHistory, useLocation } from 'react-router';
import { Order } from 'core/order/Order';
import { AddonFeatureManager } from 'core';
import {
  DefaultStateModalModel,
  StateModalModel,
} from 'containers/Common/StateModalModel';
import _ from 'lodash';
import { History } from 'history';
import { createCampaignBindInfo } from 'core/binding/CampaignBindInfo';
import {
  CreativeManagementAction,
  RtbCreativeManagementStateFactory,
} from './CampaignCreativeManagement/CreativeManagementStateFactory';

const campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager();

type CampaignsData = {
  campaignsLoading: boolean;
  campaigns: RtbCampaignBasic[];
  fetchCampaigns: () => Promise<void>;
};

export const useFetchCampaigns = (order: Order): CampaignsData => {
  const [campaigns, setCampaigns] = useState<RtbCampaignBasic[]>([]);
  const { loading, callAPIs } = useCallAPI();

  const fetchCampaigns = useCallback(() => {
    return callAPIs(
      [() => campaignManager.getCampaignsOfOrder(order.id)],
      (newCamapigns: RtbCampaignBasic[]) => {
        setCampaigns(newCamapigns);
      },
    );
  }, [callAPIs, order]);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  return {
    campaignsLoading: loading,
    campaigns,
    fetchCampaigns,
  };
};

export const useCreativeManageModal = (
  order: Order,
  addonFeatureManager: AddonFeatureManager,
  campaigns: any[],
  refetchCampaigns: () => Promise<void>,
): StateModalModel | undefined => {
  const location = useLocation();
  const history = useHistory();

  const creativeManageModal = useMemo(() => {
    const query = new URLSearchParams(location.search);
    const factoryModel = getCreativeManagementStateFactory(
      query,
      history,
      campaigns,
      order,
      addonFeatureManager,
    );
    if (!factoryModel) {
      return;
    }

    return new DefaultStateModalModel(factoryModel, (dirty) => {
      history.replace(location.pathname);
      if (dirty) refetchCampaigns();
    });
  }, [
    location,
    history,
    campaigns,
    order,
    addonFeatureManager,
    refetchCampaigns,
  ]);

  return creativeManageModal;
};

const getCreativeManagementStateFactory = (
  query: URLSearchParams,
  history: History,
  campaigns,
  order,
  addonFeatureManager,
) => {
  const campaignIdsParam = query.get('campaignIds');
  const actionParam = query.get('action');
  if (!actionParam || !campaignIdsParam) {
    return undefined;
  }

  const campaignIds = _.defaultTo(campaignIdsParam, '').split(',');
  const creativeBindData = campaigns.map(createCampaignBindInfo);
  const campaignBindList = getCampaignBindList(creativeBindData, campaignIds);
  const allActions: string[] = Object.values(CreativeManagementAction);

  if (campaignBindList.length === 0 || !allActions.includes(actionParam)) {
    return;
  }
  return new RtbCreativeManagementStateFactory(
    campaigns.filter((campaign) =>
      campaignIds.includes(campaign.id.toString()),
    ),
    order.advertiserId,
    actionParam,
    addonFeatureManager,
    order.orderNumber,
    history,
  );
};

function getCampaignBindList(creativeBindData, campaignIds) {
  return creativeBindData
    ? creativeBindData.filter(
        (campaign) =>
          campaign.id && campaignIds.includes(campaign.id.toString()),
      )
    : [];
}
